import type { AutomationCategoryResponse } from '@reach/shared-types/automation-category';
import { MAX_ENGAGE_LIBRARY_SYNC_ITEMS } from '@reach/shared-types/engage-library';
import { TenantSyncStateEnum } from '@reach/shared-types/tenant-filter';
import {
  EngageLibraryFilters,
  EngageLibraryItemType,
  EngageLibraryItemTypeEnum,
  EngageLibraryResponse,
  EngageLibraryRowModel,
  EngageLibrarySyncActionEnum,
  EngageLibrarySyncFailureReasonEnum,
  EngageLibrarySyncRequest,
  EngageLibrarySyncStatusEnum,
  EngageLibrarySyncResultItem,
  EngageStatusFilter,
  MembershipLockReasonEnum,
  PendingMembershipChange,
} from './engage-library-types';

export const TENANT_SYNC_STATE_LABEL: Record<TenantSyncStateEnum, string> = {
  [TenantSyncStateEnum.DRAFT]: 'Draft',
  [TenantSyncStateEnum.NOT_SYNCED]: 'Not synced',
  [TenantSyncStateEnum.AVAILABLE]: 'Available',
  [TenantSyncStateEnum.ACTIVE]: 'Active',
  [TenantSyncStateEnum.FAILED]: 'Failed',
  [TenantSyncStateEnum.SYNCING]: 'Syncing…',
};

export interface StatusFilterOption {
  value: EngageStatusFilter;
  label: string;
}

const AUTOMATION_STATUS_OPTIONS: StatusFilterOption[] = [
  { value: 'all', label: 'All statuses' },
  { value: TenantSyncStateEnum.ACTIVE, label: 'Active' },
  { value: TenantSyncStateEnum.AVAILABLE, label: 'Available' },
  { value: TenantSyncStateEnum.DRAFT, label: 'Draft' },
  { value: TenantSyncStateEnum.NOT_SYNCED, label: 'Not synced' },
  { value: TenantSyncStateEnum.FAILED, label: 'Failed' },
];

const SEGMENT_STATUS_OPTIONS: StatusFilterOption[] = [
  { value: 'all', label: 'All statuses' },
  { value: TenantSyncStateEnum.ACTIVE, label: 'Active' },
  { value: TenantSyncStateEnum.DRAFT, label: 'Draft' },
  { value: TenantSyncStateEnum.NOT_SYNCED, label: 'Not synced' },
  { value: TenantSyncStateEnum.FAILED, label: 'Failed' },
];

export function formatLastModified(updatedAt: string | null): string {
  if (!updatedAt) return '—';
  const parsed = new Date(updatedAt);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function statusFilterOptionsForType(type: EngageLibraryItemType): StatusFilterOption[] {
  return type === EngageLibraryItemTypeEnum.AUTOMATION
    ? AUTOMATION_STATUS_OPTIONS
    : SEGMENT_STATUS_OPTIONS;
}

export const engageLibraryTabId = (panelId: string, value: EngageLibraryItemType) =>
  `${panelId}-${value}-tab`;

export function toRowModels(
  library: EngageLibraryResponse,
  type: EngageLibraryItemType,
): EngageLibraryRowModel[] {
  if (type === EngageLibraryItemTypeEnum.AUTOMATION) {
    return library.automations.map(automation => ({
      id: automation.id,
      type,
      name: automation.name,
      description: automation.description,
      updatedAt: automation.updatedAt,
      tenantSyncState: automation.tenantSyncState,
      retryable: automation.retryable,
      removable: true,
      categories: automation.categories,
    }));
  }
  return library.segments.map(segment => ({
    id: segment.canonicalVersionId,
    type,
    name: segment.name,
    description: segment.description,
    updatedAt: segment.updatedAt,
    tenantSyncState: segment.tenantSyncState,
    retryable: segment.retryable,
    removable: segment.removable,
    categories: [],
  }));
}

/** Every automation and segment in the library, for state that spans both type tabs. */
export function toLibraryRowModels(library: EngageLibraryResponse): EngageLibraryRowModel[] {
  return [
    ...toRowModels(library, EngageLibraryItemTypeEnum.AUTOMATION),
    ...toRowModels(library, EngageLibraryItemTypeEnum.SEGMENT),
  ];
}

/** Shows rows queued by an automation sync job as Syncing until the library catches up. */
export function withOptimisticSyncing(
  rows: EngageLibraryRowModel[],
  syncingIds: Set<string>,
): EngageLibraryRowModel[] {
  return rows.map(row =>
    syncingIds.has(row.id) ? { ...row, tenantSyncState: TenantSyncStateEnum.SYNCING } : row,
  );
}

const matchesSearch = (row: EngageLibraryRowModel, search: string): boolean => {
  const normalized = search.trim().toLowerCase();
  if (!normalized) return true;
  const haystack = `${row.name} ${row.description ?? ''}`.toLowerCase();
  return haystack.includes(normalized);
};

const matchesCategories = (row: EngageLibraryRowModel, categoryIds: string[]): boolean => {
  const isFilteringByCategory = categoryIds.length > 0;
  if (!isFilteringByCategory) return true;
  return row.categories.some(category => categoryIds.includes(category.id));
};

export function filterRows(
  rows: EngageLibraryRowModel[],
  { search, statusFilter, categoryIds }: EngageLibraryFilters,
): EngageLibraryRowModel[] {
  return rows.filter(row => {
    const statusMatches = statusFilter === 'all' || row.tenantSyncState === statusFilter;
    return statusMatches && matchesSearch(row, search) && matchesCategories(row, categoryIds);
  });
}

/** The distinct categories on the given rows, sorted by name, for the category filter. */
export function categoriesInUse(rows: EngageLibraryRowModel[]): AutomationCategoryResponse[] {
  const categoriesById = new Map(
    rows.flatMap(row => row.categories).map(category => [category.id, category]),
  );
  return Array.from(categoriesById.values()).sort((left, right) =>
    left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }),
  );
}

export function countByStatus(
  rows: EngageLibraryRowModel[],
  options: StatusFilterOption[],
): Record<EngageStatusFilter, number> {
  const counts: Record<EngageStatusFilter, number> = {
    all: 0,
    [TenantSyncStateEnum.DRAFT]: 0,
    [TenantSyncStateEnum.NOT_SYNCED]: 0,
    [TenantSyncStateEnum.AVAILABLE]: 0,
    [TenantSyncStateEnum.ACTIVE]: 0,
    [TenantSyncStateEnum.FAILED]: 0,
    [TenantSyncStateEnum.SYNCING]: 0,
  };
  for (const option of options) {
    counts[option.value] =
      option.value === 'all'
        ? rows.length
        : rows.filter(row => row.tenantSyncState === option.value).length;
  }
  return counts;
}

export const SYNCED_TENANT_STATES: TenantSyncStateEnum[] = [
  TenantSyncStateEnum.ACTIVE,
  TenantSyncStateEnum.AVAILABLE,
  TenantSyncStateEnum.FAILED,
  TenantSyncStateEnum.SYNCING,
];

/** Whether the tenant has the item on the server, which is the row checkbox's unchanged state. */
export function tenantHasItem(row: EngageLibraryRowModel): boolean {
  return SYNCED_TENANT_STATES.includes(row.tenantSyncState);
}

export function syncedCount(rows: EngageLibraryRowModel[]): number {
  return rows.filter(tenantHasItem).length;
}

/** Why the row's membership checkbox is disabled, or undefined when the partner can change it. */
export function membershipLockReason(row: EngageLibraryRowModel): MembershipLockReasonEnum | undefined {
  if (row.tenantSyncState === TenantSyncStateEnum.SYNCING) return MembershipLockReasonEnum.SYNCING;
  const isRequiredOnTenant = !row.removable && tenantHasItem(row);
  if (isRequiredOnTenant) return MembershipLockReasonEnum.REQUIRED_SEGMENT;
  return undefined;
}

/** The row checkbox's state: the pending change when there is one, otherwise the server's. */
export function isMembershipChecked(
  row: EngageLibraryRowModel,
  pendingAction: EngageLibrarySyncActionEnum | undefined,
): boolean {
  return pendingAction ? pendingAction === EngageLibrarySyncActionEnum.SYNC : tenantHasItem(row);
}

export function engageLibraryRowKey(row: EngageLibraryRowModel): string {
  return `${row.type}:${row.id}`;
}

/**
 * Drops desired memberships the server already matches, rows that left the library, and rows that
 * became locked. Returns the same Map when nothing was dropped so callers can skip a state update.
 */
export function pruneSettledMembershipChanges(
  desiredMembershipByKey: Map<string, boolean>,
  rows: EngageLibraryRowModel[],
): Map<string, boolean> {
  const rowsByKey = new Map(rows.map(row => [engageLibraryRowKey(row), row]));
  const openChanges = Array.from(desiredMembershipByKey).filter(([key, desiredMembership]) => {
    const row = rowsByKey.get(key);
    if (!row) return false;
    const serverDiffers = tenantHasItem(row) !== desiredMembership;
    return serverDiffers && !membershipLockReason(row);
  });
  const nothingSettled = openChanges.length === desiredMembershipByKey.size;
  return nothingSettled ? desiredMembershipByKey : new Map(openChanges);
}

/** The pending add/remove for each row whose desired membership differs from the server. */
export function toPendingMembershipChanges(
  desiredMembershipByKey: Map<string, boolean>,
  rows: EngageLibraryRowModel[],
): PendingMembershipChange[] {
  return rows
    .filter(row => {
      const desiredMembership = desiredMembershipByKey.get(engageLibraryRowKey(row));
      return desiredMembership !== undefined && desiredMembership !== tenantHasItem(row);
    })
    .map(row => ({
      row,
      action: tenantHasItem(row) ? EngageLibrarySyncActionEnum.UNSYNC : EngageLibrarySyncActionEnum.SYNC,
    }));
}

/** Builds the PATCH body that applies the given membership changes. */
export function toSyncRequest(changes: PendingMembershipChange[]): EngageLibrarySyncRequest {
  const itemsFor = (action: EngageLibrarySyncActionEnum) =>
    changes
      .filter(change => change.action === action)
      .map(change => ({ type: change.row.type, id: change.row.id }));
  return {
    sync: itemsFor(EngageLibrarySyncActionEnum.SYNC),
    unsync: itemsFor(EngageLibrarySyncActionEnum.UNSYNC),
  };
}

/** Splits pending changes into batches that each fit the server's per-request limit. */
export function toSyncBatches(changes: PendingMembershipChange[]): PendingMembershipChange[][] {
  const batches: PendingMembershipChange[][] = [];
  for (let start = 0; start < changes.length; start += MAX_ENGAGE_LIBRARY_SYNC_ITEMS) {
    batches.push(changes.slice(start, start + MAX_ENGAGE_LIBRARY_SYNC_ITEMS));
  }
  return batches;
}

export interface SyncResultSummary {
  syncedCount: number;
  queuedCount: number;
  failures: Extract<EngageLibrarySyncResultItem, { status: EngageLibrarySyncStatusEnum.FAILED }>[];
}

export function summarizeSyncResults(results: EngageLibrarySyncResultItem[]): SyncResultSummary {
  const summary: SyncResultSummary = {
    syncedCount: 0,
    queuedCount: 0,
    failures: [],
  };
  for (const result of results) {
    if (result.status === EngageLibrarySyncStatusEnum.FAILED) summary.failures.push(result);
    else if (result.status === EngageLibrarySyncStatusEnum.SYNCED) summary.syncedCount += 1;
    else summary.queuedCount += 1;
  }
  return summary;
}

const FAILURE_REASON_LABEL: Record<EngageLibrarySyncFailureReasonEnum, string> = {
  [EngageLibrarySyncFailureReasonEnum.SEGMENT_IN_USE]: 'in use by an automation',
  [EngageLibrarySyncFailureReasonEnum.SYSTEM_SEGMENT]: 'required for every tenant',
  [EngageLibrarySyncFailureReasonEnum.CONFLICTING_TEMPLATE]:
    'another reputation automation already targets this tenant',
  [EngageLibrarySyncFailureReasonEnum.NOT_FOUND]: 'no longer available',
  [EngageLibrarySyncFailureReasonEnum.INTERNAL_ERROR]: 'failed unexpectedly',
};

export function describeSyncSummary(summary: SyncResultSummary): string {
  const parts: string[] = [];
  if (summary.syncedCount > 0) parts.push(`${summary.syncedCount} applied`);
  if (summary.queuedCount > 0) parts.push(`${summary.queuedCount} queued`);
  if (summary.failures.length > 0) {
    const reasons = Array.from(new Set(summary.failures.map(f => FAILURE_REASON_LABEL[f.reason])));
    parts.push(`${summary.failures.length} blocked (${reasons.join(', ')})`);
  }
  return parts.join(' · ');
}
